import React from "react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from "@phosphor-icons/react";

const SIZES = [25, 50, 100, 200];

export default function Pagination({ page, pageSize, total, totalPages, onPageChange, onPageSizeChange }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-[#E5E5E5] bg-white"
      data-testid="pagination-bar"
    >
      <div className="flex items-center gap-4 text-xs text-neutral-600">
        <span data-testid="pagination-range">
          Showing{" "}
          <span className="font-mono tabular-nums text-neutral-900">{from}</span>–
          <span className="font-mono tabular-nums text-neutral-900">{to}</span> of{" "}
          <span className="font-mono tabular-nums text-neutral-900">{total}</span>
        </span>
        <div className="flex items-center gap-2">
          <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-semibold">
            Rows
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger
              data-testid="pagination-page-size"
              className="h-8 w-[80px] text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIZES.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={!canPrev}
          onClick={() => onPageChange(1)}
          data-testid="pagination-first-btn"
          aria-label="First page"
        >
          <CaretDoubleLeft size={14} weight="bold" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          data-testid="pagination-prev-btn"
          aria-label="Previous page"
        >
          <CaretLeft size={14} weight="bold" />
        </Button>
        <span
          className="px-3 h-8 flex items-center text-xs font-mono tabular-nums text-neutral-700"
          data-testid="pagination-page-indicator"
        >
          {page} / {totalPages || 1}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          data-testid="pagination-next-btn"
          aria-label="Next page"
        >
          <CaretRight size={14} weight="bold" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={!canNext}
          onClick={() => onPageChange(totalPages)}
          data-testid="pagination-last-btn"
          aria-label="Last page"
        >
          <CaretDoubleRight size={14} weight="bold" />
        </Button>
      </div>
    </div>
  );
}
